import React from "react";
import { Carousel, Spinner, Container, Badge } from "react-bootstrap";
import { motion } from "framer-motion";
import { FaArrowRight, FaExpandAlt, FaCalendarAlt } from "react-icons/fa";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi";
import useProgramData from "../hooks/useProgramData";

const TimelineCarousel = () => {
  const { data: events, loading, error } = useProgramData("timelineData");

  if (loading) return (
    <div className="loader-container">
      <Spinner animation="border" variant="info" />
      <span className="ms-3 text-info fw-bold">Syncing Timeline...</span>
    </div>
  );

  if (error) return (
    <div className="error-box">Error: {error}</div>
  );

  return (
    <section className="timeline-master-wrapper">
      <Container>
        <motion.div 
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="timeline-main-card"
        >
          <Carousel
            fade
            indicators={true}
            prevIcon={<div className="nav-btn-modern"><HiOutlineChevronLeft /></div>}
            nextIcon={<div className="nav-btn-modern"><HiOutlineChevronRight /></div>}
            className="h-100"
          >
            {events.map((event, index) => (
              <Carousel.Item key={index} interval={6000}>
                <div className="carousel-flex-layout">
                  
                  {/* Left: Interactive Media Stage */}
                  <div className="media-stage">
                    <div className="abstract-shape"></div>
                    <motion.div 
                      whileHover={{ scale: 1.02 }}
                      className="image-container"
                      onClick={() => window.open(event.image, "_blank")}
                    >
                      <img src={event.image} alt={event.title} className="hero-img" />
                      <div className="glass-overlay">
                        <FaExpandAlt className="expand-icon" />
                      </div>
                    </motion.div>
                  </div>

                  {/* Right: Content Engine */}
                  <div className="content-stage">
                    <div className="content-meta">
                      <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <Badge bg="info" className="status-badge">Latest Update</Badge>
                      </motion.div>
                      <motion.div 
                        className="date-pill"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                      >
                        <FaCalendarAlt className="me-2 text-cyan" />
                        {event.date}
                      </motion.div>
                    </div>

                    <motion.h2 
                      initial={{ y: 30, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{ delay: 0.4, type: "spring", damping: 12 }}
                      className="display-title"
                    >
                      {event.title}
                    </motion.h2>

                    <motion.p 
                      className="description-text"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                    >
                      {event.description}
                    </motion.p>

                    <motion.div 
                      className="action-row"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                    >
                      <motion.button 
                        whileHover={{ scale: 1.05, boxShadow: "0 0 20px rgba(0, 206, 209, 0.4)" }}
                        whileTap={{ scale: 0.95 }}
                        className="btn-cta-modern"
                        onClick={() => window.open(event.image, "_blank")}
                      >
                        Explore Details <FaArrowRight className="ms-3" />
                      </motion.button>
                      <div className="pagination-count">
                        <span className="current">0{index + 1}</span> 
                        <span className="separator">/</span> 
                        <span className="total">0{events.length}</span>
                      </div>
                    </motion.div>
                  </div>

                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        </motion.div>
      </Container>

      <style>{`
        .timeline-master-wrapper {
          padding: 60px 0;
          background: #f8fafc; /* Subtle light background to contrast the dark card */
        }

        .loader-container {
          height: 400px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .timeline-main-card {
          background: #001e3c; /* Deep Sukkur IBA Navy */
          border-radius: 30px;
          min-height: 480px;
          overflow: hidden;
          box-shadow: 0 40px 100px -20px rgba(0, 30, 60, 0.4);
          border: 1px solid rgba(255, 255, 255, 0.08);
          position: relative;
        }

        .carousel-flex-layout {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          min-height: 480px;
        }

        /* --- Media Stage --- */
        .media-stage {
          position: relative;
          background: #00152b;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 40px;
          overflow: hidden;
        }

        .abstract-shape {
          position: absolute;
          width: 300px;
          height: 300px;
          background: radial-gradient(circle, rgba(0, 206, 209, 0.15) 0%, transparent 70%);
          filter: blur(40px);
          z-index: 1;
        }

        .image-container {
          position: relative;
          z-index: 2;
          cursor: pointer;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 30px 60px rgba(0,0,0,0.5);
        }

        .hero-img {
          height: 340px;
          width: 100%;
          object-fit: cover;
          transition: 0.6s cubic-bezier(0.2, 1, 0.3, 1);
        }

        .glass-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0, 206, 209, 0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: 0.3s;
        }

        .image-container:hover .glass-overlay { opacity: 1; }
        .expand-icon { color: white; font-size: 2rem; transform: scale(0.8); transition: 0.3s; }
        .image-container:hover .expand-icon { transform: scale(1); }

        /* --- Content Stage --- */
        .content-stage {
          padding: 60px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          color: white;
          background: linear-gradient(135deg, rgba(255,255,255,0.02) 0%, transparent 100%);
        }

        .content-meta {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 25px;
        }

        .status-badge {
          font-weight: 500;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.75rem;
          background-color: #00ced1 !important;
        }

        .date-pill {
          font-size: 0.85rem;
          color: #94a3b8;
          display: flex;
          align-items: center;
        }

        .display-title {
          font-size: 2.5rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 20px;
          letter-spacing: -1px;
        }

        .description-text {
          color: #cbd5e1;
          font-size: 1.1rem;
          line-height: 1.7;
          margin-bottom: 35px;
        }

        .action-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: auto;
        }

        .btn-cta-modern {
          background: #ffffff;
          color: #001e3c;
          border: none;
          padding: 12px 30px;
          border-radius: 12px;
          font-weight: 700;
          font-size: 0.95rem;
          display: flex;
          align-items: center;
          transition: 0.3s;
        }

        .btn-cta-modern:hover {
          background: #00ced1;
          color: white;
        }

        .pagination-count {
          font-family: 'Space Mono', monospace;
          font-size: 1.2rem;
          font-weight: 700;
          color: #00ced1;
        }
        .pagination-count span { color: rgba(255,255,255,0.2); }

        /* --- Custom Controls --- */
        .nav-btn-modern {
          width: 50px;
          height: 50px;
          background: rgba(255,255,255,0.05);
          backdrop-filter: blur(10px);
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          color: white;
          transition: 0.3s;
          border: 1px solid rgba(255,255,255,0.1);
        }

        .carousel-control-prev, .carousel-control-next { opacity: 1; width: 10%; }
        .nav-btn-modern:hover { background: #00ced1; color: white; transform: translateY(-3px); }

        @media (max-width: 991px) {
          .timeline-master-wrapper { padding: 40px 0 20px; }
          .timeline-main-card { 
            min-height: auto; 
            border-radius: 20px; 
            margin: 0 10px; 
            border: 1px solid rgba(255, 255, 255, 0.05);
          }
          .carousel-flex-layout { grid-template-columns: 1fr; min-height: auto; }
          
          .media-stage { 
            padding: 12px; 
            min-height: 200px; 
            background: #000c1a;
          }
          .hero-img { height: 180px; border-radius: 12px; width: 100%; max-width: 100%; margin: 0 auto; object-fit: cover; }
          .abstract-shape { display: none; }
          
          .content-stage { 
            padding: 20px 15px; 
            text-align: center; 
            background: #001e3c;
          }
          .content-meta { justify-content: center; margin-bottom: 8px; gap: 8px; }
          .status-badge { font-size: 0.6rem; padding: 4px 10px; }
          .date-pill { font-size: 0.7rem; color: #94a3b8; }
 
          .display-title { 
            font-size: 1.2rem; 
            margin-bottom: 8px; 
            line-height: 1.3;
            max-width: 95%;
            margin-left: auto;
            margin-right: auto;
          }
          .description-text { 
            font-size: 0.78rem; 
            margin-bottom: 15px; 
            line-height: 1.4;
            color: #cbd5e1;
            max-width: 95%;
            margin-left: auto;
            margin-right: auto;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
          
          .action-row { 
            flex-direction: column-reverse; 
            gap: 12px; 
            margin-top: 5px;
          }
          .btn-cta-modern { 
            width: auto; 
            min-width: 160px;
            align-self: center;
            padding: 8px 18px;
            font-size: 0.85rem;
            border-radius: 10px;
          }
          .pagination-count { font-size: 0.85rem; opacity: 0.4; }
 
          /* Mobile Navigation */
          .nav-btn-modern { width: 34px; height: 34px; font-size: 1rem; border-radius: 8px; }
          .carousel-control-prev, .carousel-control-next { width: 10%; }
          .carousel-indicators { display: none; }
        }
 
        @media (max-width: 576px) {
          .timeline-master-wrapper { padding: 15px 0; }
          .display-title { font-size: 1.15rem; }
          .media-stage { min-height: 170px; padding: 10px; }
          .hero-img { height: 160px; }
          .description-text { -webkit-line-clamp: 2; }
          .btn-cta-modern { padding: 8px 16px; font-size: 0.8rem; }
        }
      `}</style>
    </section>
  );
};

export default TimelineCarousel;